import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/layout/SEOHead';
import { FadeIn } from '@/components/motion/FadeIn';
import { getContact } from '@/lib/content';
import Studio, { frontmatter as studioFrontmatter } from '../../content/studio.mdx';

const AWARDS = [
  { year: 2025, title: 'Best of Houzz · Design', body: 'Recognized by Houzz for design excellence.' },
  { year: 2025, title: 'Recommended on Houzz', body: 'Verified client recommendation badge.' },
  { year: 2024, title: '250+ Ideabook Saves', body: 'Project work saved across Houzz user ideabooks.' },
];

const PRESS = [
  { outlet: 'Brevard Living', headline: 'Where coastal restraint meets craft', date: 'Spring 2025' },
  { outlet: 'Space Coast Daily', headline: 'A quieter kind of luxury in Melbourne', date: 'Fall 2024' },
  { outlet: 'Houzz', headline: 'Featured: Coastal Monolith — a residence that disappears', date: '2024' },
];

const TESTIMONIALS = [
  {
    body: 'We told Brian we wanted a house that disappeared. He came back with one that does — and somehow contains more of who we are than any house we have ever lived in.',
    attribution: 'Coastal Monolith homeowners — Cocoa Beach',
  },
  {
    body: 'Ember would not be the room it is without Brian. The hearth, the steel screen behind the bar, the deliberate dimness — every choice serves the evening, not the renovation.',
    attribution: 'Chef-owner, Ember Supperclub — Melbourne',
  },
  {
    body: 'He kept asking us how we actually live, not how we wanted the house to look. The result is the first home we have ever felt finished in.',
    attribution: 'Indialantic family — Limewash Cottage',
  },
];

export function AboutPage() {
  const contact = getContact();
  const headline = (studioFrontmatter['headline'] as string) ?? 'Studio.';

  return (
    <>
      <SEOHead
        title="Studio · Brian Ellison Design"
        description="The studio of Brian Ellison — multidisciplinary interior designer based in Melbourne, Florida. Best of Houzz 2025."
        canonical="https://brianellisondesign.com/about"
      />
      <section className="page-hero">
        <div className="container">
          <FadeIn>
            <p className="eyebrow">{contact.company}</p>
            <h1 className="page-hero__title">{headline}</h1>
            <p className="subtitle">
              Multidisciplinary luxury interior design in Melbourne, Florida. Founded 2017.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="container section about-body drop-cap">
        <FadeIn>
          <Studio />
        </FadeIn>
      </section>

      <p className="ornament container" aria-hidden="true">§</p>

      <section className="container section about-awards">
        <FadeIn>
          <p className="eyebrow">Awards & recognition</p>
          <h2 className="about-section-heading">Quietly recognized.</h2>
        </FadeIn>
        <ul className="about-awards__list">
          {AWARDS.map((a, i) => (
            <FadeIn key={i} as="li" delay={i * 60}>
              <div className="about-awards__year display-italic">{a.year}</div>
              <div>
                <div className="about-awards__title">{a.title}</div>
                <p className="caption">{a.body}</p>
              </div>
            </FadeIn>
          ))}
        </ul>
      </section>

      <section className="container section about-press">
        <FadeIn>
          <p className="eyebrow">Press</p>
          <h2 className="about-section-heading">In conversation.</h2>
        </FadeIn>
        <ul className="about-press__list">
          {PRESS.map((p, i) => (
            <FadeIn key={i} as="li" delay={i * 60}>
              <div className="about-press__date">{p.date}</div>
              <div>
                <div className="about-press__outlet">{p.outlet}</div>
                <div className="display-italic about-press__headline">"{p.headline}"</div>
              </div>
            </FadeIn>
          ))}
        </ul>
        <p className="caption" style={{ marginTop: 'var(--space-5)' }}>
          For press inquiries, <Link to="/contact" className="text-link">get in touch</Link>.
        </p>
      </section>

      <section className="container section about-testimonials">
        <FadeIn>
          <p className="eyebrow">In their words</p>
          <h2 className="about-section-heading">From the clients.</h2>
        </FadeIn>
        <div className="about-testimonials__grid">
          {TESTIMONIALS.map((t, i) => (
            <FadeIn key={i} delay={i * 80}>
              <figure className="about-testimonial">
                <blockquote className="pull-quote">{t.body}</blockquote>
                <figcaption className="caption about-testimonial__attribution">— {t.attribution}</figcaption>
              </figure>
            </FadeIn>
          ))}
        </div>
      </section>

      <section className="container section about-cta">
        <FadeIn>
          <p className="subtitle">
            Considering a project? <Link to="/contact" className="text-link">Send an inquiry</Link>.
          </p>
        </FadeIn>
      </section>
    </>
  );
}
